import { BottomSheet, Button, Card, Icon, List, Loader, PageLayout, Typography } from '@bharatpe/bharat-ui';
import AppHeader from 'components/atoms/AppHeader';
import React, { useEffect, useState } from 'react';
import {
	RESET_SELECTED_SIM,
	SET_HOME_MODAL_VISIBILITY,
	SET_ONBOARDING_REDIRECTION_PAGE,
	SET_UPI_STEP,
	useUpiData,
	useUpiStep,
} from 'Slices';
import {
	checkPermissions,
	customSubScribeBackButton,
	getURLParameter,
	OpenAppSettings,
	SavePermissions,
} from 'utils/nativeSupport';
import changePinIcon from 'assets/icons/Autopay/changePinIcon.svg';
import forgotPinIcon from 'assets/icons/Autopay/forgotPinIcon.svg';
import unlinkBankIcon from 'assets/icons/Autopay/unlinkBankIcon.svg';
import accountBalanceIcon from 'assets/icons/Autopay/accountBalanceIcon.svg';
import cardIcon from 'assets/icons/Autopay/cardIcon.svg';
import { CheckCircle, WarningRounded } from '@bharatpe/bharat-ui/web/Icons';
import AlertIcon from 'assets/icons/Autopay/danger.svg';

import { ChangeUPIPin, forgetUPIPin, UPICheckBalance, UPIRemoveBankAccount } from 'utils/nativeUpiHandler';
import Toasty from 'components/atoms/Toasty';
import { sendEvent } from 'utils/Sentry';
import WrongPinModal from 'components/organisms/Modals/WrongPinModal';
import UpiPoweredBy from 'components/atoms/UpiPoweredBy';
import { isEmpty } from 'utils/Utils';
import { triggerUPIUserDetails, useUPIUserDetails } from 'Slices/GetUPIUserDetails';
import { useDispatch } from 'react-redux';
import history from 'utils/History';
import { ONBOARDING_REDIRECTION_PAGE, PERMISSIONS, UPI_ERROR_CODE, UPISteps } from 'utils/CONSTANT';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import useUpiStatus from 'Hooks/useUpiStatus';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';
import AllModals from 'containers/UPIContainer/Modals';
import PermissionModal from 'containers/UPIContainer/Modals/PermissionModal';

const BankAccountDetails: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const [amount, setAmount] = useState<string>('');
	const [showBankBalanceModal, setShowBankModal] = useState(false);
	const [showSetPinConfirmationModal, setSetPinConfirmationModal] = useState(false);
	const [showPinChangedSuccessfulModal, setPinChangedSuccessfulModal] = useState(false);
	const [showDelinkAccountConfirmationModal, setShowDeinkAccountConfirmationModal] = useState(false);
	const [showDelinkAccountDeniedModal, setShowDeinkAccountDeniedModal] = useState(false);
	const [showWrongPinModal, setShowWrongPinModal] = useState<boolean>(false);
	const [showPermissionModal, setShowPermissionModal] = useState<boolean>(false);

	const [retryFunction, setRetryFunction] = useState<string>('');
	const upiStepDetails = useUpiStep();
	const { languageData } = useLanguageConfig();
	const { upiStep, upiErr } = upiStepDetails;
	const upiStatus = useUpiStatus(ONBOARDING_REDIRECTION_PAGE.PROFILE);

	const dispatch = useDispatch();
	const upiUserData = useUPIUserDetails();
	const { user_banks } = upiUserData?.data || [];

	const upiData = useUpiData();
	const selectedBank = user_banks?.find(
		(bank: any) => bank?.bank_account_id == getURLParameter(window.location.href, 'bank-account-id'),
	);
	const { is_primary, account, bank_logo } = (selectedBank as any) || {};

	useEffect(() => {
		if ((upiStatus?.upiErr || upiStatus?.upiStep) && isEmpty(upiStepDetails) && !upiStatus?.loading) {
			dispatch(SET_UPI_STEP({ upiStep: upiStatus?.upiStep, upiErr: upiStatus?.upiErr, loading: upiStatus?.loading }));
		}
	}, [dispatch, upiStatus]);

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AC_Details',
			Module: 'Page',
			Action: 'Viewed',
		});
	}, []);

	useEffect(() => {
		if (isEmpty(upiUserData)) {
			dispatch(triggerUPIUserDetails());
		}
	}, [dispatch, upiUserData]);

	const setReclaimFlow = async () => {
		if (selectedBank) {
			history.push({
				pathname: '/upi/reclaim-bank-list',
			});
		} else {
			Toasty.error('Something went wrong !!!');
		}
	};

	const checkUPIOnboarding = (fn: any) => {
		const permissions = checkPermissions();
		if (!isEmpty(upiStep)) {
			dispatch(
				SET_ONBOARDING_REDIRECTION_PAGE({
					source: ONBOARDING_REDIRECTION_PAGE.PROFILE,
					searchParam: getURLParameter(window.location.href, 'bank-account-id'),
				}),
			);
		}
		permissions.then((data: any) => {
			if (!data.READ_PHONE_STATE || !data.SEND_SMS) {
				setShowPermissionModal(true);
			} else {
				if (upiErr) {
					Toasty.error(upiErr);
					dispatch(SET_HOME_MODAL_VISIBILITY({ somethingWentWrong: true }));
					return;
				}

				switch (upiStep) {
					case UPISteps.UPGRADE:
						dispatch(RESET_SELECTED_SIM());
						history.push({ pathname: '/upi/sim-binding' });
						break;
					case UPISteps.UPI_ONBOARDING_COMPLETE:
						fn();
						break;
					case UPISteps.RECLAIM_STATUS:
						setReclaimFlow();
						break;
					case UPISteps.BANK_SCREEN:
						history.push({
							pathname: '/upi/bank-selection',
						});
						break;
				}
			}
		});
		return;
	};

	const getBankCardStrip = () => {
		if (is_primary)
			return {
				children: <Typography className="text-center green-text-color">{languageData.PRIMARY_ACCOUNT}</Typography>,
				variant: 'success',
				showIcon: false,
				className: 'flex-justify-center',
			};
		return undefined;
	};
	const profile_list = [
		{
			title: languageData.CHANGE_PIN,
			leftContentAlignTop: true,
			leftIcon: {
				icon: changePinIcon,
				height: '30px',
				width: '30px',
			},
		},
		{
			title: languageData.FORGET_PIN_UPI,
			leftContentAlignTop: true,
			leftIcon: {
				icon: forgotPinIcon,
				height: '30px',
				width: '30px',
			},
		},
		{
			title: languageData.UNLINK_BANK_ACCOUNT,
			leftContentAlignTop: true,
			leftIcon: {
				icon: unlinkBankIcon,
				height: '30px',
				width: '30px',
			},
		},
	];
	const handleCheckBalanceClick = async () => {
		try {
			logAnalyticsEvent({
				trigger_point: 'Clicks Check Balance',
				Product: 'AutoPayUPI',
				Flow: 'Setting',
				Screen: 'AC_Details',
				Module: 'Account_Card',
				Element: 'CTA',
				Action: 'Tapped',
				isPrimary: is_primary,
				bankName: selectedBank?.['acc-provider-name'],
				accNumber: account,
				ctaType: 'checkBalance',
			});
			if (!isEmpty(selectedBank) && selectedBank != undefined) {
				const amount = await UPICheckBalance(selectedBank);
				if (amount) {
					setAmount(amount as string);
					setShowBankModal(true);
				}
			}
		} catch (error: any) {
			if (error.errorCode == 'ZM') {
				setShowWrongPinModal(true);
				setRetryFunction('balance');
			} else if (error.errorCode == 'Z6') {
				Toasty.error('Exceeded number of pin tries');
			} else if (error?.error === 'USER_ABORTED') {
				//
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				sendEvent(error);
				Toasty.error(error?.error);
			} else {
				sendEvent(error);
				Toasty.error('Something went wrong!!');
			}
		}
	};
	const handleChangePinClick = async () => {
		try {
			if (!isEmpty(selectedBank) && selectedBank != undefined) {
				const isSuccess = await ChangeUPIPin(selectedBank);
				if (isSuccess) setPinChangedSuccessfulModal(true);
			}
		} catch (error: any) {
			if (error.errorCode == 'ZM') {
				setShowWrongPinModal(true);
				setRetryFunction('change');
			} else if (error.errorCode == 'Z6') {
				Toasty.error('Exceeded number of pin tries');
			} else if (error?.error === 'USER_ABORTED') {
				//
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				Toasty.error(error?.error);
				sendEvent(error);
			} else {
				sendEvent(error);
				Toasty.error('Something went wrong!!');
			}
		}
	};
	const handleForgetPinClick = async () => {
		try {
			if (!isEmpty(selectedBank) && selectedBank != undefined) {
				const isSuccess = await forgetUPIPin(selectedBank);
				if (isSuccess) setPinChangedSuccessfulModal(true);
			}
		} catch (error: any) {
			if (error.errorCode == 'ZM') {
				setShowWrongPinModal(true);
				setRetryFunction('forget');
			} else if (error.errorCode == 'Z6') {
				Toasty.error('Exceeded number of pin tries');
			} else if (error?.error === 'USER_ABORTED') {
				//
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				sendEvent(error);
				Toasty.error(error?.error);
			} else {
				sendEvent(error);
				Toasty.error('Something went wrong!!');
			}
		}
	};
	const handleDeleteAccountClick = async () => {
		logAnalyticsEvent({
			trigger_point: 'Modal View',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Unlink_BA',
			Module: 'Modal',
			Action: 'Viewed',
		});
		setShowDeinkAccountConfirmationModal(true);
	};
	const handleShowBalanceModalClose = () => {
		setShowBankModal(false);
	};
	const handleSetPinConfirmationClose = () => {
		setSetPinConfirmationModal(false);
	};
	const handleSetPinConfirmationConfirm = () => {
		handleForgetPinClick();
		setSetPinConfirmationModal(false);
	};
	const handlePinChnageConfirmationClose = () => {
		setPinChangedSuccessfulModal(false);
	};
	const handleDelinkAccountConfirmationClick = async () => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Unlink',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Unlink_BA',
			Module: 'Modal',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'Unlink',
		});
		setShowDeinkAccountConfirmationModal(false);
		try {
			if (!isEmpty(selectedBank) && selectedBank != undefined) {
				const isSuccess = await UPIRemoveBankAccount(selectedBank);
				if (isSuccess) {
					dispatch(triggerUPIUserDetails());
					history.push({ pathname: '/account/delink-success' });
				} else Toasty.error('Something went wrong!!');
			}
		} catch (error: any) {
			if (error?.error === 'USER_ABORTED') {
				//
			} else if (error?.errorCode === '712') {
				Toasty.error(error?.error || 'Something went wrong!!');
			} else if (error?.errorCode === '1058') {
				setShowDeinkAccountDeniedModal(true);
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				Toasty.error(error?.error);
				sendEvent(error);
			} else {
				sendEvent(error);
				Toasty.error('Something went wrong!!');
			}
		}
		// }
	};

	const handleDelinkAccountCancelClick = () => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Cancel',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Unlink_BA',
			Module: 'Modal',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'Cancel',
		});
		setShowDeinkAccountConfirmationModal(false);
	};

	const handlePinReset = async () => {
		setShowWrongPinModal(false);
		try {
			const isSuccess = await forgetUPIPin(selectedBank);
			if (isSuccess) setPinChangedSuccessfulModal(true);
		} catch (error: any) {
			if (error.errorCode == 'ZM') {
				setShowWrongPinModal(true);
			} else if (error.errorCode == 'Z6') {
				Toasty.error('Exceeded number of pin tries');
			} else if (error?.error === 'USER_ABORTED') {
				//
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				Toasty.error(error?.error);
				sendEvent(error);
			} else {
				Toasty.error('Something went wrong!!');
				sendEvent(error);
			}
		}
	};

	const showPinConfermationModal = () => {
		setSetPinConfirmationModal(true);
	};

	const handleRetry = () => {
		setShowWrongPinModal(false);
		switch (retryFunction) {
			case 'balance':
				return handleCheckBalanceClick();
			case 'forget':
				return handleForgetPinClick();
			case 'change':
				handleChangePinClick();
		}
	};
	const handleOnClick = (val: any, id: number) => {
		if (id == 0) {
			logAnalyticsEvent({
				trigger_point: 'Clicks Change PIN',
				Product: 'AutoPayUPI',
				Flow: 'Setting',
				Screen: 'AC_Details',
				Module: 'CTA_Cards',
				Element: 'CTA',
				Action: 'Tapped',
				isPrimary: is_primary,
				bankName: selectedBank?.['acc-provider-name'],
				accNumber: account,
				ctaType: 'changePIN',
			});
			checkUPIOnboarding(handleChangePinClick);
		} else if (id == 1) {
			logAnalyticsEvent({
				trigger_point: 'Clicks Forget UPI PIN',
				Product: 'AutoPayUPI',
				Flow: 'Setting',
				Screen: 'AC_Details',
				Module: 'CTA_Cards',
				Element: 'CTA',
				Action: 'Tapped',
				isPrimary: is_primary,
				bankName: selectedBank?.['acc-provider-name'],
				accNumber: account,
				ctaType: 'forgetPIN',
			});
			checkUPIOnboarding(showPinConfermationModal);
		} else if (id == 2) {
			logAnalyticsEvent({
				trigger_point: 'Clicks Forget UPI PIN',
				Product: 'AutoPayUPI',
				Flow: 'Setting',
				Screen: 'AC_Details',
				Module: 'CTA_Cards',
				Element: 'CTA',
				Action: 'Tapped',
				isPrimary: is_primary,
				bankName: selectedBank?.['acc-provider-name'],
				accNumber: account,
				ctaType: 'unlinkAccount',
			});
			checkUPIOnboarding(handleDeleteAccountClick);
		}
	};

	const backEventAction = () => {
		logAnalyticsEvent({
			trigger_point: `Clicks on Back Button`,
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'AC_Details',
			Element: 'back',
			Action: 'Tapped',
			isPrimary: is_primary,
			bankName: selectedBank?.['acc-provider-name'],
			accNumber: account,
			source: 'appHeader',
		});
	};

	const handleDelinkAccountDeniedPrimaryButtonClick = () => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Okay',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Loan_found',
			Module: 'Modal',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'Okay',
		});
		setShowDeinkAccountDeniedModal(false);
	};

	useEffect(() => {
		customSubScribeBackButton(() => {
			onBack();
		});
	}, [history]);

	const handleSavePermission = async () => {
		const permissions = checkPermissions();
		permissions.then(async (data) => {
			if (!data.READ_PHONE_STATE || !data.SEND_SMS || !data.READ_SMS) {
				const permissionSaved = await SavePermissions(PERMISSIONS);
				setShowPermissionModal(false);
				if (permissionSaved.READ_PHONE_STATE && permissionSaved.SEND_SMS && permissionSaved.READ_SMS) {
					logAnalyticsEvent({
						trigger_point: `Click on 'SETUP AUTOPAY'`,
						Product: 'AutoPayUPI',
						Flow: 'SetupUPI',
						Screen: 'Setup',
						Module: 'Permission_Modal',
						Element: 'CTA',
						Action: 'Tapped',
						ctaType: 'setupAutopay',
					});
				} else {
					OpenAppSettings();
				}
			} else {
				setShowPermissionModal(false);
			}
		});
	};

	const onBack = () => {
		history.replace({ pathname: '/account/bank-list' });
	};

	return loading || selectedBank == undefined ? (
		<Loader loading />
	) : (
		<PageLayout className="white-background ">
			<>
				<AppHeader leftIconAction={onBack} title={languageData.ACCOUNT_DETAILS} backEventAction={backEventAction} />
				<AllModals upiOnboardingSource={ONBOARDING_REDIRECTION_PAGE.PROFILE} />
				<div className="mx5 mt6">
					<Card strip={getBankCardStrip()} className="p4 flex flex-column">
						<div className="flex flex-row flex-justify flex-center">
							<div>
								<Typography variant="SemiBold300" className="text-left">
									<>
										{selectedBank?.['acc-provider-name']} {account}
									</>
								</Typography>
								<Typography variant="Light100" className="text-left grey mt1">
									{languageData.SAVING_ACCOUNT}
								</Typography>
							</div>
							<div className="br3 grey-border p2">
								<img src={bank_logo} loading="lazy" height={32} width={32} />
							</div>
						</div>
						<Button
							variant="outlined"
							className="width-75 mt4"
							onClick={() => checkUPIOnboarding(handleCheckBalanceClick)}>
							{languageData.CHECK_BALANCE}
						</Button>
					</Card>
					<List list={profile_list} className={'mt6'} onClick={(val: any, id: number) => handleOnClick(val, id)} />
				</div>
				<div className="upi-powered">
					<UpiPoweredBy />
				</div>
				<BottomSheet
					visible={showBankBalanceModal}
					showCloseButton={true}
					onClose={handleShowBalanceModalClose}
					rounded>
					<div className="p4 flex flex-column">
						<img src={accountBalanceIcon} width={50} height={50} />
						<Typography variant="Regular200" className="grey mt2">
							{languageData.YOUR_ACCOUNT_BALANCE}
						</Typography>
						<Typography variant="Bold700" className="mt1 mb4">
							<>₹{amount}</>
						</Typography>
						<Button onClick={handleShowBalanceModalClose} className="mt4">
							{languageData.DONE}
						</Button>
					</div>
				</BottomSheet>

				<BottomSheet visible={showSetPinConfirmationModal} showCloseButton={false} rounded>
					<div className="p4 flex flex-column">
						<img src={cardIcon} width={40} height={32} />
						<Typography variant="Bold500" className="mt2">
							{languageData.SET_PIN_WITH_DEBIT_CARD}
						</Typography>
						<Typography variant="Regular200" className="grey mt1 mb4">
							{languageData.KINDLY_PROCEED_ONLY_IF_YOU_HAVE_THE_SAME_MOBILE_NUMBER_MOBILENUMBER_REGISTERED_WITH_DEBIT_CARD_KEEP_YOUR_DEBIT_CARD_WITH_YOU_TO_VARIFY_16_DIGIT_NUMBER.replace(
								'{0}',
								upiUserData?.data?.mobile,
							)}
						</Typography>
						<div className="flex flex-row mt4">
							<Button onClick={handleSetPinConfirmationClose} variant="outlined" className="mr2 width-50">
								{languageData.BACK}
							</Button>
							<Button onClick={handleSetPinConfirmationConfirm} className="width-50">
								{languageData.CONTINUE}
							</Button>
						</div>
					</div>
				</BottomSheet>
				<BottomSheet visible={showPinChangedSuccessfulModal} showCloseButton={false} rounded>
					<div className="p4 flex flex-column">
						<Icon icon={CheckCircle} className="mr2 success-logo" width={40} height={40} />
						<Typography variant="Bold500" className="mt2">
							{languageData.PIN_SUCCESSFULLY_CHANGED}
						</Typography>
						<Typography variant="Regular200" className="grey mt1 mb4">
							{languageData.YOU_HAVE_SUCCESSFULLY_UPDATED_YOUR_UPI_PIN_FOR_ACCOUNTNAME_ACCOUNT_ACCOUNTNUMBER.replace(
								'{0}',
								selectedBank?.['acc-provider-name'],
							).replace('{1}', account)}
						</Typography>
						<Button onClick={handlePinChnageConfirmationClose} className="mt4">
							{languageData.DONE}
						</Button>
					</div>
				</BottomSheet>
				<BottomSheet visible={showDelinkAccountConfirmationModal} showCloseButton={false} rounded>
					<div className="p4 flex flex-column">
						<Icon icon={WarningRounded} className="mr2 failed-logo" width={40} height={40} />
						<Typography variant="Bold500" className="mt2">
							{languageData.ARE_YOU_SURE_QUESTIONMARK}
						</Typography>
						<Typography variant="Regular200" className="grey mt1 mb4">
							{
								languageData.IF_YOU_UNLINK_YOUR_ACCOUNT_YOU_WILL_NO_LONGER_BE_ABLE_TO_USE_IT_FOR_PAYMENTS_ON_BHARATPE_DO_YOU_STILL_WANT_TO_PROCEED_WITH_THE_UNLINKING_YOUR_BANK_ACCOUNT_QUESTIONMARK
							}
						</Typography>
						<div className="flex flex-row mt4">
							<Button
								onClick={handleDelinkAccountConfirmationClick}
								color="danger"
								variant="outlined"
								className="mr2 width-50">
								{languageData.UNLINK}
							</Button>

							<Button onClick={handleDelinkAccountCancelClick} className="width-50">
								{languageData.CANCEL}
							</Button>
						</div>
					</div>
				</BottomSheet>
				<BottomSheet visible={showDelinkAccountDeniedModal} showCloseButton={false} rounded>
					<div className="p4 flex flex-column">
						<Icon icon={WarningRounded} className="mr2 failed-logo" width={40} height={40} />
						<Typography variant="Bold500" className="mt2">
							{languageData.NOT_ALLOWED_EXCLAMATIONMARK}
						</Typography>
						<Typography variant="Regular200" className="grey mt1 mb4">
							{
								languageData.YOU_CANNOT_DELETE_THIS_BANK_ACCOUNT_BECAUSE_IT_IS_LINKED_TO_AUTOPAY_PAYMENTS_FOR_AN_ONGOING_LOAN
							}
						</Typography>
						<div className="flex flex-row mt4">
							<Button onClick={handleDelinkAccountDeniedPrimaryButtonClick} className="width-100">
								{languageData.OKAY}
							</Button>
						</div>
					</div>
				</BottomSheet>
				{showPermissionModal && (
					<PermissionModal
						handleClick={handleSavePermission}
						btnText={languageData.CONTINUE}
						handleModalClose={() => {
							setShowPermissionModal(false);
						}}
					/>
				)}
				{showWrongPinModal && (
					<WrongPinModal
						handleClickPrimary={handlePinReset}
						handleClickSecondary={handleRetry}
						handleModalClose={() => setShowWrongPinModal(false)}
						title={languageData.WRONG_UPI_PIN_ENTERED}
						description={
							languageData.PLEASE_USE_THE_CORRECT_PIN_AND_TRY_AGAIN_YOUR_BANK_WILL_TEMPORARILY_BLOCK_UPI_PAYMENTS_IF_YOU_ENTER_THE_WRONG_PIN_TOO_MANY_TIMES
						}
						primaryButtonName={languageData.RESET_PIN}
						secondaryButtonName={languageData.RETRY}
						icon={AlertIcon}
						type={'info'}
					/>
				)}
			</>
		</PageLayout>
	);
};
export default BankAccountDetails;
